import { useRouter } from 'next/router'
import { useCallback } from 'react'

import { ParsedUrlQuery } from 'querystring'

export type UseQueryPramParams = {
  name: string
  as?: string
  replace?: boolean
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
  persist?: boolean | string[]
}

function persistQuery(params: ParsedUrlQuery, persist: boolean | string[]) {
  if (Array.isArray(persist)) {
    return persist.reduce((acc, key) => {
      if (params[key]) acc[key] = params[key]
      return acc
    }, {})
  }

  if (persist) {
    return params
  }

  return {}
}

/**
 * Hook to handle specified query parameter value with ability to persist query state
 */
function useQueryParam<T extends string = string>({
  name,
  as,
  replace,
  shallow,
  locale,
  scroll,
  persist,
}: UseQueryPramParams) {
  const router = useRouter()
  const param = router.query[name] as T

  const setParam = useCallback(
    (value?: string) => {
      const method = replace ? router.replace : router.push

      const query = { ...persistQuery(router.query, persist), [name]: value }

      if (typeof value === 'undefined' || value === null) {
        delete query[name]
      }

      return method({ query }, as, {
        shallow,
        locale,
        scroll,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [as, locale, name, persist, replace, scroll, shallow, router, router.query]
  )

  return [param, setParam] as const
}

export default useQueryParam
